import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {
        console.warn("Payment status check failed, retrying", e);
      }
      if (!active) return;
      if (++tries.current >= 15) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen paper-noise flex items-center justify-center px-6">
      <div className="max-w-lg w-full border border-foreground/15 bg-background p-10 shadow-[8px_8px_0_0_hsl(var(--primary))]" data-testid="payment-status-card">
        {state === "checking" && (<>
          <Loader2 className="w-10 h-10 animate-spin text-primary mb-6" />
          <h1 className="font-serif-display text-4xl tracking-tight mb-3">Confirming payment…</h1>
          <p className="text-muted-foreground">Hold tight while we hear back from Stripe.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-10 h-10 text-primary mb-6" strokeWidth={1.5} />
          <div className="font-mono-label text-muted-foreground mb-2">Payment confirmed</div>
          <h1 className="font-serif-display text-4xl tracking-tight mb-3" data-testid="payment-success-title">Welcome to your new plan.</h1>
          <p className="text-muted-foreground mb-8">Your upgrade is active. Go upload something worth reading.</p>
          <Link to="/app" data-testid="payment-go-dashboard"><Button className="rounded-sm bg-foreground text-background hover:bg-primary btn-invert">Go to library →</Button></Link>
        </>)}
        {(state === "error" || state === "timeout") && (<>
          <XCircle className="w-10 h-10 text-destructive mb-6" strokeWidth={1.5} />
          <h1 className="font-serif-display text-4xl tracking-tight mb-3" data-testid="payment-error-title">
            {state === "timeout" ? "Still processing." : "Payment not completed."}
          </h1>
          <p className="text-muted-foreground mb-8">
            {state === "timeout" ? "Stripe hasn't confirmed yet. Refresh this page in a minute." : "Nothing was charged. You can try again from the pricing page."}
          </p>
          <Link to="/#pricing"><Button variant="outline" className="rounded-sm">Back to pricing</Button></Link>
        </>)}
      </div>
    </div>
  );
}
